import {
  AdditiveBlending,
  Color,
  type ColorSpace,
  DataTexture,
  DoubleSide,
  LinearFilter,
  LinearMipmapLinearFilter,
  LineBasicMaterial,
  MeshBasicMaterial,
  MeshStandardMaterial,
  NoColorSpace,
  RGBAFormat,
  RepeatWrapping,
  ShaderMaterial,
  UniformsLib,
  UniformsUtils,
  Vector2,
  SRGBColorSpace,
  UnsignedByteType,
} from "three";

import {
  AUTHORED_WINDOW_NIGHT_INTENSITY,
  BUILDING_WINDOW_NIGHT_INTENSITY_FACTORS,
} from "../../config/buildings.ts";

import type {
  VilniusBuildingVisualSpec,
} from "../../world/buildings/index.ts";
import type { RuntimeTileLodLevel } from "../../world/schema/runtimeTile.ts";
import type {
  OldTownDoorLeafStyle,
} from "../../config/oldTownBuildingArt.ts";
import type { StreetSurfaceFamily } from "../../config/streets.ts";
import type {
  PedestrianSurfaceClass,
  RoadSurfaceClass,
} from "../../world/streets.ts";
import {
  SEASONAL_SURFACE_PALETTES,
} from "../../config/seasons.ts";
import {
  TRAFFIC_SIGNAL_RENDER_CONFIG,
  type TrafficSignalPhaseGroup,
} from "../../config/trafficSignals.ts";
import { VISUAL_REALISM_CONFIG } from "../../config/visualRealism.ts";
import { WATER_RENDER_CONFIG } from "../../config/water.ts";
import type { EnvironmentSeason } from "../environment.ts";

export interface StreetSurfaceMaterialSet {
  readonly carriageway: MeshStandardMaterial;
  readonly junction: MeshStandardMaterial;
  readonly sidewalk: MeshStandardMaterial;
  readonly curb: MeshStandardMaterial;
  readonly pedestrian: MeshStandardMaterial;
}

export interface DistantTransportMaterialSet {
  readonly carriageway: MeshStandardMaterial;
  readonly pedestrian: MeshStandardMaterial;
}

export interface RailwayMaterialSet {
  readonly ballast: MeshStandardMaterial;
  readonly sleeper: MeshStandardMaterial;
  readonly steel: MeshStandardMaterial;
  readonly catenary: MeshStandardMaterial;
  readonly distant: MeshStandardMaterial;
}

export interface TrafficSignalLensMaterialSet {
  readonly red: MeshStandardMaterial;
  readonly amber: MeshStandardMaterial;
  readonly green: MeshStandardMaterial;
}

export type GroundCoverMaterialFamily =
  | "woodland"
  | "scrub"
  | "meadow"
  | "garden"
  | "park";

export interface CityMaterialLibrary {
  readonly terrain: Readonly<Record<RuntimeTileLodLevel, MeshStandardMaterial>>;
  readonly landCover: MeshStandardMaterial;
  readonly groundCover: Readonly<
    Record<GroundCoverMaterialFamily, MeshStandardMaterial>
  >;
  readonly hedge: MeshStandardMaterial;
  readonly parcelBoundary: MeshStandardMaterial;
  readonly parkedVehicleBody: MeshStandardMaterial;
  readonly streetSurfaces: Readonly<
    Record<StreetSurfaceFamily, StreetSurfaceMaterialSet>
  >;
  /** Materials for source-backed GRPK DANGA codes; district defaults stay separate. */
  readonly officialRoadSurfaces: Readonly<
    Record<Exclude<RoadSurfaceClass, "default">, MeshStandardMaterial>
  >;
  /** Materials for separately mapped OSM sidewalk surface tags. */
  readonly mappedPedestrianSurfaces: Readonly<
    Record<Exclude<PedestrianSurfaceClass, "default">, MeshStandardMaterial>
  >;
  readonly distantTransport: Readonly<
    Record<StreetSurfaceFamily, DistantTransportMaterialSet>
  >;
  readonly railway: RailwayMaterialSet;
  readonly water: ShaderMaterial;
  readonly diagnosticBuildingPlan: MeshStandardMaterial;
  readonly lodBuildingBounds: Readonly<Record<1 | 2, MeshStandardMaterial>>;
  readonly collisionLine: LineBasicMaterial;
  readonly tileDiagnosticLine: LineBasicMaterial;
  readonly window: MeshStandardMaterial;
  readonly windowUnlit: MeshStandardMaterial;
  readonly panelWindowUnlit: MeshStandardMaterial;
  readonly oldTownDoorLeaves: Readonly<
    Record<OldTownDoorLeafStyle, MeshStandardMaterial>
  >;
  readonly oldTownPlanter: MeshStandardMaterial;
  readonly oldTownPortalLanternBulb: MeshStandardMaterial;
  readonly buildingPassageLining: MeshStandardMaterial;
  readonly tunnelConcrete: MeshStandardMaterial;
  readonly tunnelJoint: MeshStandardMaterial;
  readonly tunnelMetal: MeshStandardMaterial;
  readonly tunnelLight: MeshStandardMaterial;
  readonly tunnelDamp: MeshStandardMaterial;
  readonly lampBulb: MeshStandardMaterial;
  readonly lampPool: MeshBasicMaterial;
  readonly streetPole: MeshStandardMaterial;
  readonly busStopBlue: MeshStandardMaterial;
  readonly busStopIvory: MeshStandardMaterial;
  readonly busStopGlass: MeshStandardMaterial;
  readonly busStopSeat: MeshStandardMaterial;
  readonly busStopTactile: MeshStandardMaterial;
  readonly roadMarking: MeshStandardMaterial;
  readonly laneMarking: MeshStandardMaterial;
  readonly roadSignBlue: MeshStandardMaterial;
  readonly roadSignIvory: MeshStandardMaterial;
  readonly roadSignInk: MeshStandardMaterial;
  readonly roadSignRed: MeshStandardMaterial;
  readonly roadSignYellow: MeshStandardMaterial;
  readonly roadSignBack: MeshStandardMaterial;
  readonly trafficSignalHousing: MeshStandardMaterial;
  readonly trafficSignalLenses: Readonly<
    Record<TrafficSignalPhaseGroup, TrafficSignalLensMaterialSet>
  >;
  readonly treeTrunk: MeshStandardMaterial;
  readonly treeBranch: MeshStandardMaterial;
  readonly treeBirchTrunk: MeshStandardMaterial;
  readonly treeEvergreenTrunk: MeshStandardMaterial;
  readonly treeSoil: MeshStandardMaterial;
  readonly treeCrownDark: MeshStandardMaterial;
  readonly treeCrownLight: MeshStandardMaterial;
  readonly treeEvergreenDark: MeshStandardMaterial;
  readonly treeEvergreenLight: MeshStandardMaterial;
  readonly treeBlossom: MeshStandardMaterial;
  readonly treeSnow: MeshStandardMaterial;
  facade(spec: Readonly<VilniusBuildingVisualSpec>): MeshStandardMaterial;
  blankFacade(
    spec: Readonly<VilniusBuildingVisualSpec>,
  ): MeshStandardMaterial;
  architecturalTrim(
    spec: Readonly<VilniusBuildingVisualSpec>,
  ): MeshStandardMaterial;
  panelJoint(
    spec: Readonly<VilniusBuildingVisualSpec>,
  ): MeshStandardMaterial;
  roof(spec: Readonly<VilniusBuildingVisualSpec>): MeshStandardMaterial;
  setSeason(season: EnvironmentSeason): void;
  setNightFactor(nightFactor: number): void;
  setSurfaceWetness(wetness01: number): void;
  advanceWaterTime(deltaSeconds: number): void;
  advanceTrafficSignalTime(deltaSeconds: number): void;
  dispose(): void;
}

interface FacadeAtlas {
  readonly diffuse: DataTexture;
  readonly emissive: DataTexture;
  readonly roughness: DataTexture;
}

export interface CityMaterialLibraryOptions {
  readonly visualRealismEnabled?: boolean;
}

interface WettableSurfaceState {
  readonly material: MeshStandardMaterial;
  readonly dryColor: Color;
  readonly dryRoughness: number;
  readonly wetRoughness: number;
  readonly darkening: number;
}

const ATLAS_SIZE = 128;

function makeTexture(
  name: string,
  size: number,
  pixels: Uint8Array,
  repeat = 1,
  colorSpace: ColorSpace = SRGBColorSpace,
): DataTexture {
  const texture = new DataTexture(
    pixels,
    size,
    size,
    RGBAFormat,
    UnsignedByteType,
  );
  texture.name = name;
  texture.colorSpace = colorSpace;
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(repeat, repeat);
  texture.magFilter = LinearFilter;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.anisotropy = 4;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

function createNormalTextureFromLuma(
  name: string,
  source: Readonly<DataTexture>,
  gradientScale: number,
): DataTexture {
  const image = source.image as {
    readonly data: Uint8Array;
    readonly width: number;
    readonly height: number;
  };
  const pixels = new Uint8Array(image.width * image.height * 4);
  const luma = (x: number, y: number): number => {
    const wrappedX = (x + image.width) % image.width;
    const wrappedY = (y + image.height) % image.height;
    const offset = (wrappedY * image.width + wrappedX) * 4;
    return (
      (image.data[offset] ?? 0) * 0.2126 +
      (image.data[offset + 1] ?? 0) * 0.7152 +
      (image.data[offset + 2] ?? 0) * 0.0722
    ) / 255;
  };
  for (let y = 0; y < image.height; y += 1) {
    for (let x = 0; x < image.width; x += 1) {
      const tangentX =
        (luma(x - 1, y) - luma(x + 1, y)) * gradientScale;
      const tangentY =
        (luma(x, y - 1) - luma(x, y + 1)) * gradientScale;
      const inverseLength = 1 / Math.hypot(tangentX, tangentY, 1);
      const offset = (y * image.width + x) * 4;
      pixels[offset] = Math.round((tangentX * inverseLength * 0.5 + 0.5) * 255);
      pixels[offset + 1] = Math.round(
        (tangentY * inverseLength * 0.5 + 0.5) * 255,
      );
      pixels[offset + 2] = Math.round(inverseLength * 255);
      pixels[offset + 3] = 255;
    }
  }
  return makeTexture(
    name,
    image.width,
    pixels,
    source.repeat.x,
    NoColorSpace,
  );
}

function materialNormalScale(
  enabled: boolean,
  scale: number,
): Vector2 {
  const applied = enabled ? scale : 0;
  return new Vector2(applied, applied);
}

function createPlasterTextureSet(
  name: string,
  enableRealismTreatment: boolean,
): Readonly<{
  diffuse: DataTexture;
  normal: DataTexture;
}> {
  const size = 64;
  const pixels = new Uint8Array(size * size * 4);
  const fullTurn = Math.PI * 2;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const offset = (y * size + x) * 4;
      // Integer-frequency waves tile without a hard seam. Their smooth
      // gradients avoid the broad diagonal/block steps that a quantized hash
      // field exposed on otherwise quiet lime-plaster side walls.
      const plasterMottle =
        Math.sin(fullTurn * x / size) * 1.6 +
        Math.cos(fullTurn * y / size) * 1.25 +
        Math.sin(fullTurn * (x + y) * 2 / size) * 0.8 +
        Math.cos(fullTurn * (x * 3 - y * 2) / size) * 0.45;
      const value = enableRealismTreatment
        ? 247 + Math.round(plasterMottle)
        : 255;
      pixels[offset] = value;
      pixels[offset + 1] = value;
      pixels[offset + 2] = value;
      pixels[offset + 3] = 255;
    }
  }
  const diffuse = makeTexture(
    `${name}-diffuse`,
    size,
    pixels,
    3,
  );
  const normal = createNormalTextureFromLuma(
    `${name}-normal`,
    diffuse,
    2,
  );
  return { diffuse, normal };
}

function createFacadeAtlas(
  family: "old_town" | "soviet_panel" | "timber" | "industrial",
  enableRealismTreatment = true,
): FacadeAtlas {
  const diffuse = new Uint8Array(ATLAS_SIZE * ATLAS_SIZE * 4);
  const emissive = new Uint8Array(ATLAS_SIZE * ATLAS_SIZE * 4);
  const roughness = new Uint8Array(ATLAS_SIZE * ATLAS_SIZE * 4);
  const windowMinX = family === "old_town"
    ? 43
    : family === "timber"
      ? 40
      : family === "industrial"
        ? 35
        : 37;
  const windowMaxX = family === "old_town"
    ? 84
    : family === "timber"
      ? 87
      : family === "industrial"
        ? 92
        : 90;
  const windowMinY = family === "old_town"
    ? 31
    : family === "timber"
      ? 30
      : family === "industrial"
        ? 22
        : 35;
  const windowMaxY = family === "old_town"
    ? 91
    : family === "timber"
      ? 92
      : family === "industrial"
        ? 99
        : 88;
  const frameWidth = family === "old_town"
    ? 5
    : family === "timber"
      ? 4
      : 3;
  // Preserve the former family-specific hash ranges for the exact rollback
  // texture. In the realism path even the smaller panel range sparkled at
  // gameplay distance, so the authored geometry carries the wall rhythm.
  const grainRange = family === "soviet_panel"
    ? 7
    : family === "timber"
      ? 9
      : family === "industrial"
        ? 7
        : 13;
  const grainOffset = family === "soviet_panel"
    ? 3
    : family === "timber"
      ? 4
      : family === "industrial"
        ? 3
        : 6;
  const plaster = family === "old_town"
    ? 238
    : family === "timber"
      ? 226
      : family === "industrial"
        ? 224
        : 218;
  const realisticGlass = family === "soviet_panel"
    ? VISUAL_REALISM_CONFIG.panelFacadeGlass
    : VISUAL_REALISM_CONFIG.facadeGlass;
  for (let y = 0; y < ATLAS_SIZE; y += 1) {
    for (let x = 0; x < ATLAS_SIZE; x += 1) {
      const offset = (y * ATLAS_SIZE + x) * 4;
      const withinFrame =
        x >= windowMinX - frameWidth &&
        x <= windowMaxX + frameWidth &&
        y >= windowMinY - frameWidth &&
        y <= windowMaxY + frameWidth;
      const withinGlass =
        x >= windowMinX &&
        x <= windowMaxX &&
        y >= windowMinY &&
        y <= windowMaxY;
      const mullion =
        withinGlass &&
        (Math.abs(x - (windowMinX + windowMaxX) / 2) <= 1 ||
          (family !== "soviet_panel" &&
            Math.abs(y - (windowMinY + windowMaxY) / 2) <= 1));
      const sill =
        x >= windowMinX - frameWidth - 2 &&
        x <= windowMaxX + frameWidth + 2 &&
        y >= windowMaxY + frameWidth &&
        y <= windowMaxY + frameWidth + 3;
      const panelSeam = family === "soviet_panel" &&
        (x <= 2 || y <= 2);
      // Horizontal board courses keep the low timber districts legible from
      // every exposed side. Frontage-specific trim and entrances are still
      // authored as geometry; this atlas supplies only the restrained shared
      // timber grain needed on rear and side walls.
      const timberBoardSeam = family === "timber" &&
        !withinFrame &&
        y % 12 <= 1;
      const industrialBrickCourse = family === "industrial" &&
        !withinFrame &&
        (y % 8 === 0 ||
          (x + (Math.floor(y / 8) % 2) * 12) % 24 === 0);
      // Pixel-frequency hash grain sparkles at gameplay distance. At the
      // accepted atlas resolution a broad one-luma mottle was visually
      // indistinguishable from clean plaster, yet still added construction
      // work. The realism path therefore keeps wall fields quiet and relies
      // on the authored seams, courses, trim, and geometry for material scale.
      const grain = enableRealismTreatment
        ? 0
        : ((x * 17 + y * 29 + x * y * 3) % grainRange) - grainOffset;
      // Historic-centre lime plaster is never one perfectly even digital
      // colour. A broad, low-contrast patch field sits under the fine grain;
      // it is intentionally subtle enough to keep the miniature art target.
      const oldTownPlasterMottle =
        enableRealismTreatment && family === "old_town"
        ? Math.round(
          Math.sin(Math.PI * 2 * x / ATLAS_SIZE) * 1.45 +
          Math.cos(Math.PI * 2 * y / ATLAS_SIZE) * 1.15 +
          Math.sin(
            Math.PI * 2 * (x + y) * 2 / ATLAS_SIZE,
          ) * 0.7,
        )
        : 0;
      const sillBottomY = windowMaxY + frameWidth + 3;
      const sillRunoffDepth = y - sillBottomY;
      const oldTownSillRunoff = enableRealismTreatment &&
        family === "old_town" &&
        !withinFrame &&
        sillRunoffDepth > 0 &&
        sillRunoffDepth <= 22 &&
        (
          Math.abs(x - (windowMinX + 5)) <=
            Math.max(1, 3 - Math.floor(sillRunoffDepth / 8)) ||
          Math.abs(x - (windowMaxX - 5)) <=
            Math.max(1, 3 - Math.floor(sillRunoffDepth / 8))
        );
      let red = plaster + grain + oldTownPlasterMottle;
      let green = plaster + grain + Math.round(oldTownPlasterMottle * 0.82);
      let blue = plaster + grain + Math.round(oldTownPlasterMottle * 0.62);
      if (panelSeam) {
        red = 174;
        green = 176;
        blue = 174;
      } else if (timberBoardSeam) {
        red = 184 + Math.round(grain * 0.35);
        green = 186 + Math.round(grain * 0.35);
        blue = 178 + Math.round(grain * 0.25);
      } else if (industrialBrickCourse) {
        red = 174;
        green = 170;
        blue = 163;
      } else if (oldTownSillRunoff) {
        const runoffStrength = 8 - Math.floor(sillRunoffDepth / 5);
        red -= runoffStrength;
        green -= runoffStrength;
        blue -= Math.max(2, runoffStrength - 2);
      } else if (sill || (withinFrame && !withinGlass)) {
        red = family === "old_town" ? 246 : family === "timber" ? 236 : 196;
        green = family === "old_town" ? 241 : family === "timber" ? 233 : 198;
        blue = family === "old_town" ? 229 : family === "timber" ? 218 : 196;
      } else if (withinGlass) {
        const glassVariation = ((x * 5 + y * 3) % 9) - 4;
        const revealShadow = enableRealismTreatment && (
          x - windowMinX <= 2 ||
          windowMaxX - x <= 2 ||
          y - windowMinY <= 2 ||
          windowMaxY - y <= 2
        );
        const reflectedSkyGlint = enableRealismTreatment &&
          !mullion &&
          Math.abs(x - (windowMinX + (windowMaxX - windowMinX) * 0.31)) <= 1;
        red = mullion
          ? 185
          : (enableRealismTreatment ? realisticGlass.red : 118) +
            glassVariation - (revealShadow ? 11 : 0) +
            (reflectedSkyGlint ? 9 : 0);
        green = mullion
          ? 188
          : (enableRealismTreatment ? realisticGlass.green : 137) +
            glassVariation - (revealShadow ? 12 : 0) +
            (reflectedSkyGlint ? 13 : 0);
        blue = mullion
          ? 184
          : (enableRealismTreatment ? realisticGlass.blue : 143) +
            glassVariation - (revealShadow ? 9 : 0) +
            (reflectedSkyGlint ? 17 : 0);
      }
      diffuse[offset] = red;
      diffuse[offset + 1] = green;
      diffuse[offset + 2] = blue;
      diffuse[offset + 3] = 255;
      const leftPaneLit = withinGlass &&
        !mullion &&
        x < (windowMinX + windowMaxX) / 2;
      const upperRightPaneDim = family !== "soviet_panel" &&
        withinGlass &&
        !mullion &&
        x > (windowMinX + windowMaxX) / 2 &&
        y < (windowMinY + windowMaxY) / 2;
      emissive[offset] = leftPaneLit ? 255 : upperRightPaneDim ? 92 : 0;
      emissive[offset + 1] = leftPaneLit ? 178 : upperRightPaneDim ? 58 : 0;
      emissive[offset + 2] = leftPaneLit ? 86 : upperRightPaneDim ? 28 : 0;
      emissive[offset + 3] = 255;
      const roughnessLuma = !enableRealismTreatment
        ? 255
        : withinGlass && !mullion
          ? VISUAL_REALISM_CONFIG.facadeRoughnessMap.glassLuma
          : mullion
            ? VISUAL_REALISM_CONFIG.facadeRoughnessMap.mullionLuma
            : sill || (withinFrame && !withinGlass)
              ? VISUAL_REALISM_CONFIG.facadeRoughnessMap.frameLuma
              : panelSeam || timberBoardSeam || industrialBrickCourse
                ? VISUAL_REALISM_CONFIG.facadeRoughnessMap.seamLuma
                : VISUAL_REALISM_CONFIG.facadeRoughnessMap.wallLuma;
      roughness[offset] = roughnessLuma;
      roughness[offset + 1] = roughnessLuma;
      roughness[offset + 2] = roughnessLuma;
      roughness[offset + 3] = 255;
    }
  }
  const diffuseTexture = makeTexture(
    `${family}-shared-facade-atlas`,
    ATLAS_SIZE,
    diffuse,
  );
  const emissiveTexture = makeTexture(
    `${family}-shared-window-emissive-atlas`,
    ATLAS_SIZE,
    emissive,
  );
  const roughnessTexture = makeTexture(
    `${family}-shared-facade-roughness-atlas`,
    ATLAS_SIZE,
    roughness,
    1,
    NoColorSpace,
  );
  return {
    diffuse: diffuseTexture,
    emissive: emissiveTexture,
    roughness: roughnessTexture,
  };
}

function periodicTextureNoise(
  x: number,
  y: number,
  size: number,
  cells: number,
  seed: number,
): number {
  const coordinateX = (x + 0.5) / size * cells;
  const coordinateY = (y + 0.5) / size * cells;
  const cellX = Math.floor(coordinateX);
  const cellY = Math.floor(coordinateY);
  const fractionX = coordinateX - cellX;
  const fractionY = coordinateY - cellY;
  const smoothX = fractionX * fractionX * (3 - 2 * fractionX);
  const smoothY = fractionY * fractionY * (3 - 2 * fractionY);
  const sample = (offsetX: number, offsetY: number): number => {
    const wrappedX = ((cellX + offsetX) % cells + cells) % cells;
    const wrappedY = ((cellY + offsetY) % cells + cells) % cells;
    let hash = Math.imul(wrappedX + seed * 17, 0x1f123bb5) ^
      Math.imul(wrappedY + seed * 29, 0x5f356495);
    hash = Math.imul(hash ^ hash >>> 15, 0x2c1b3c6d);
    hash ^= hash >>> 12;
    return (hash >>> 0) / 0xffffffff;
  };
  const lower = sample(0, 0) * (1 - smoothX) + sample(1, 0) * smoothX;
  const upper = sample(0, 1) * (1 - smoothX) + sample(1, 1) * smoothX;
  return lower * (1 - smoothY) + upper * smoothY;
}

function createSurfaceTexture(
  name: string,
  kind:
    | "old-town-cobble"
    | "old-town-junction"
    | "asphalt-road"
    | "asphalt-path"
    | "old-town-paver"
    | "concrete-slab"
    | "cement-road"
    | "aggregate-road"
    | "gravel-road"
    | "wood-road"
    | "roof"
    | "metal-roof"
    | "grass"
    | "tree-bark"
    | "soil"
    | "light",
  repeatMultiplier = 1,
  enableRealismTreatment = true,
): DataTexture {
  // One metre of road UV space covers eight texels. Keep the macro road tile
  // wide enough that a repair does not visibly stamp every few parked cars.
  const size = kind === "asphalt-road" ? 512 : kind === "grass" ? 128 : 64;
  const pixels = new Uint8Array(size * size * 4);
  const realisticJointLuma = VISUAL_REALISM_CONFIG.surfaceJointLuma;
  const roofJointLuma = enableRealismTreatment
    ? realisticJointLuma.roof
    : 154;
  const cobbleJointLuma = enableRealismTreatment
    ? realisticJointLuma.cobble
    : 152;
  const cementJointLuma = enableRealismTreatment
    ? realisticJointLuma.cement
    : 169;
  const woodJointLuma = enableRealismTreatment
    ? realisticJointLuma.wood
    : 116;
  const paverJointLuma = enableRealismTreatment
    ? realisticJointLuma.paver
    : 181;
  const concreteJointLuma = enableRealismTreatment
    ? realisticJointLuma.concrete
    : 190;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const offset = (y * size + x) * 4;
      if (kind === "light") {
        const distance = Math.hypot(
          (x + 0.5) / size - 0.5,
          (y + 0.5) / size - 0.5,
        ) / 0.5;
        const alpha = Math.max(0, 1 - distance);
        pixels[offset] = 255;
        pixels[offset + 1] = 202;
        pixels[offset + 2] = 126;
        pixels[offset + 3] = Math.round(alpha * alpha * 255);
        continue;
      }
      const grain = ((x * 19 + y * 31 + x * y * 7) % 17) - 8;
      const stagger = Math.floor(y / 8) % 2 === 0 ? 0 : 6;
      const fanRow = Math.floor(y / 32);
      const fanX = (x + (fanRow % 2) * 16) % 32 - 16;
      const fanY = y % 32;
      const fanRadius = Math.hypot(fanX, fanY);
      const fanAngle = Math.atan2(fanY, fanX);
      const fanRingJoint = fanRadius % 6.5 <= 1;
      const fanRadialJoint =
        Math.abs(Math.sin((fanAngle + fanRow * 0.09) * 12)) < 0.16;
      const cobbleMortar =
        kind === "old-town-cobble" &&
        (fanRingJoint || fanRadialJoint);
      const junctionSettJoint =
        kind === "old-town-junction" &&
        (
          y % 10 <= 1 ||
          (x + (Math.floor(y / 10) % 2) * 5) % 10 <= 1
        );
      const asphaltAggregateHash = x * 11 + y * 23 + x * y;
      const asphaltAggregate = kind === "asphalt-road"
        ? asphaltAggregateHash % 113 === 0
        : kind === "asphalt-path" && asphaltAggregateHash % 43 <= 1;
      const repairEdgeNoise = ((x * 17 + y * 37 + x * y * 3) % 13) - 6;
      const asphaltRepairA = kind === "asphalt-road" &&
        Math.hypot((x - 94) / 34, (y - 176) / 14) <
          1 + repairEdgeNoise * 0.008;
      const asphaltRepairB = kind === "asphalt-road" &&
        x >= 315 + repairEdgeNoise * 0.35 &&
        x <= 382 + repairEdgeNoise * 0.25 &&
        y >= 302 + repairEdgeNoise * 0.4 &&
        y <= 335 + repairEdgeNoise * 0.3;
      const asphaltRepairSeam = kind === "asphalt-road" &&
        x >= 55 && x <= 198 &&
        Math.abs(y - (415 + Math.sin(x * 0.13) * 2.3)) <= 1;
      const oldTownPaverJoint =
        kind === "old-town-paver" &&
        (
          y % 16 <= 1 ||
          (x + (Math.floor(y / 16) % 2) * 16) % 32 <= 1
        );
      const concreteSlabJoint =
        kind === "concrete-slab" &&
        (
          y % 24 <= 1 ||
          (x + (Math.floor(y / 24) % 2) * 24) % 48 <= 1
        );
      const cementRoadJoint = kind === "cement-road" &&
        (y % 32 <= 1 || x % 48 <= 1);
      const aggregateStone = kind === "aggregate-road" &&
        ((x * 13 + y * 29 + x * y * 3) % 31 <= 5);
      const gravelStone = kind === "gravel-road" &&
        ((x * 17 + y * 11 + x * y * 5) % 23 <= 6);
      const woodJoint = kind === "wood-road" &&
        (y % 12 <= 1 || x % 48 <= 1);
      const roofSeam = kind === "roof" &&
        (y % 8 <= 1 || (x + stagger) % 16 <= 1);
      const metalRoofSeam = kind === "metal-roof" && x % 12 <= 1;
      const grassWarpX = kind === "grass"
        ? (periodicTextureNoise(x, y, size, 3, 101) - 0.5) * 16
        : 0;
      const grassWarpY = kind === "grass"
        ? (periodicTextureNoise(x, y, size, 3, 137) - 0.5) * 16
        : 0;
      const grassPatch = kind === "grass"
        ? periodicTextureNoise(
          x + grassWarpX,
          y + grassWarpY,
          size,
          4,
          17,
        ) - 0.5
        : 0;
      const grassTuft = kind === "grass"
        ? periodicTextureNoise(x + y, y - x, size, 11, 31) - 0.5
        : 0;
      const grassFine = kind === "grass"
        ? periodicTextureNoise(x * 2 + y, y * 2 - x, size, 29, 47) - 0.5
        : 0;
      const grassWarmth = kind === "grass"
        ? periodicTextureNoise(
          x + grassWarpY,
          y - grassWarpX,
          size,
          6,
          73,
        ) - 0.5
        : 0;
      // Broad growth patches establish the lawn's overall colour, while the
      // two smaller octaves keep nearby grass from reading as a flat sheet.
      // All three are smooth periodic fields: unlike the former sparse hash
      // marks, they cannot produce isolated dark dots or tile-edge seams.
      const grassVariation = grassPatch * 30 + grassTuft * 15 + grassFine * 5;
      const barkGroove = kind === "tree-bark" &&
        ((x + Math.floor(y / 9) * 3) % 13 <= 2);
      const soilClod = kind === "soil" &&
        ((x * 17 + y * 29 + x * y * 5) % 61 <= 3);
      const base = !enableRealismTreatment &&
          (kind === "grass" || kind === "tree-bark" || kind === "soil")
        ? 255
        : kind === "roof"
        ? roofSeam ? roofJointLuma : 230 + Math.round(grain * 0.45)
        : kind === "metal-roof"
          ? metalRoofSeam ? 178 : 224 + Math.round(grain * 0.2)
        : kind === "grass"
          ? 222
        : kind === "tree-bark"
          ? barkGroove ? 198 : 238 + Math.round(grain * 0.28)
        : kind === "soil"
          ? soilClod ? 176 : 214 + Math.round(grain * 0.36)
        : kind === "old-town-cobble"
          ? cobbleMortar ? cobbleJointLuma : 205 + Math.round(grain * 0.75)
          : kind === "old-town-junction"
            ? junctionSettJoint ? 185 : 211 + Math.round(grain * 0.4)
          : kind === "asphalt-road" || kind === "asphalt-path"
            ? asphaltRepairSeam
              ? 151
              : asphaltRepairA || asphaltRepairB
                ? asphaltAggregate ? 160 : 170 + Math.round(grain * 0.35)
                : asphaltAggregate ? 166 : 181 + Math.round(grain * 0.55)
            : kind === "cement-road"
              ? cementRoadJoint
                ? cementJointLuma
                : 218 + Math.round(grain * 0.3)
              : kind === "aggregate-road"
                ? aggregateStone ? 167 : 195 + Math.round(grain * 0.8)
                : kind === "gravel-road"
                  ? gravelStone ? 154 : 187 + Math.round(grain * 0.9)
                  : kind === "wood-road"
                    ? woodJoint
                      ? woodJointLuma
                      : 174 + Math.round(grain * 0.45)
            : kind === "old-town-paver"
              ? oldTownPaverJoint
                ? paverJointLuma
                : 226 + Math.round(grain * 0.55)
              : concreteSlabJoint
                ? concreteJointLuma
                : 231 + Math.round(grain * 0.4);
      if (kind === "grass" && enableRealismTreatment) {
        pixels[offset] = Math.round(218 + grassVariation + grassWarmth * 14);
        pixels[offset + 1] = Math.round(
          226 + grassVariation + grassWarmth * 2,
        );
        pixels[offset + 2] = Math.round(
          212 + grassVariation - grassWarmth * 16,
        );
      } else {
        pixels[offset] = base;
        pixels[offset + 1] = base;
        pixels[offset + 2] = base;
      }
      pixels[offset + 3] = 255;
    }
  }
  // Roof UVs are expressed in 2.4 m world-space modules. A 1.2 repeat keeps
  // the procedural tile courses close to the small clay-tile rhythm visible
  // across Vilnius Old Town instead of reading as metre-wide shingles.
  const baseRepeat = kind === "roof"
    ? 1.2
    : kind === "metal-roof"
      ? 0.8
      : kind === "asphalt-road"
        ? 1 / 64
        : kind === "grass"
          ? 0.5
        : 1;
  return makeTexture(
    name,
    size,
    pixels,
    kind === "asphalt-road" ? baseRepeat : baseRepeat * repeatMultiplier,
  );
}

export function createCityMaterialLibrary(
  options: Readonly<CityMaterialLibraryOptions> = {},
): CityMaterialLibrary {
  const realismEnabled =
    options.visualRealismEnabled ?? VISUAL_REALISM_CONFIG.enabledByDefault;
  const oldTownAtlas = createFacadeAtlas("old_town", realismEnabled);
  const panelAtlas = createFacadeAtlas("soviet_panel", realismEnabled);
  const timberAtlas = createFacadeAtlas("timber", realismEnabled);
  const industrialAtlas = createFacadeAtlas("industrial", realismEnabled);
  const facadeNormalMaps = {
    old_town: createNormalTextureFromLuma(
      "old_town-shared-facade-normal",
      oldTownAtlas.diffuse,
      2.4,
    ),
    soviet_panel: createNormalTextureFromLuma(
      "soviet_panel-shared-facade-normal",
      panelAtlas.diffuse,
      1.7,
    ),
    timber: createNormalTextureFromLuma(
      "timber-shared-facade-normal",
      timberAtlas.diffuse,
      2,
    ),
    industrial: createNormalTextureFromLuma(
      "industrial-shared-facade-normal",
      industrialAtlas.diffuse,
      2.1,
    ),
  } as const;
  const plasterTextures = createPlasterTextureSet(
    "shared-lime-plaster-mottle",
    realismEnabled,
  );
  const surfaceTextureRepeat = realismEnabled
    ? VISUAL_REALISM_CONFIG.surfaceTextureRepeat
    : 1;
  // Ground surfaces fill most grounded frames. Pair the corrected diffuse
  // scale and quieter joints with restrained tangent-space material relief.
  const oldTownCobbleTexture = createSurfaceTexture(
    "reference-derived-old-town-fan-cobble",
    "old-town-cobble",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const asphaltRoadTexture = createSurfaceTexture(
    "procedural-asphalt-road-history",
    "asphalt-road",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const asphaltPathTexture = createSurfaceTexture(
    "reference-derived-general-asphalt",
    "asphalt-path",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const oldTownJunctionTexture = createSurfaceTexture(
    "reference-derived-old-town-junction-setts",
    "old-town-junction",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const oldTownPaverTexture = createSurfaceTexture(
    "reference-derived-old-town-stone-pavers",
    "old-town-paver",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const concreteSlabTexture = createSurfaceTexture(
    "reference-derived-general-concrete-slabs",
    "concrete-slab",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const cementRoadTexture = createSurfaceTexture(
    "official-grpk-cement-concrete-road",
    "cement-road",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const aggregateRoadTexture = createSurfaceTexture(
    "official-grpk-crushed-aggregate-road",
    "aggregate-road",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const gravelRoadTexture = createSurfaceTexture(
    "official-grpk-gravel-road",
    "gravel-road",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const woodRoadTexture = createSurfaceTexture(
    "official-grpk-wood-road",
    "wood-road",
    surfaceTextureRepeat,
    realismEnabled,
  );
  const roofTexture = createSurfaceTexture(
    "shared-clay-roof-tile",
    "roof",
    1,
    realismEnabled,
  );
  const metalRoofTexture = createSurfaceTexture(
    "shared-standing-seam-metal-roof",
    "metal-roof",
    1,
    realismEnabled,
  );
  const grassTexture = createSurfaceTexture(
    "reference-derived-vegetated-land-cover",
    "grass",
    1,
    realismEnabled,
  );
  const treeBarkTexture = createSurfaceTexture(
    "shared-tree-bark",
    "tree-bark",
    2,
    realismEnabled,
  );
  const soilTexture = createSurfaceTexture(
    "source-positioned-central-street-tree-soil",
    "soil",
    1,
    realismEnabled,
  );
  const lampPoolTexture = createSurfaceTexture(
    "shared-lamp-pool-gradient",
    "light",
    1,
    false,
  );
  const surfaceNormalMaps = {
    oldTownCobble: createNormalTextureFromLuma(
      "reference-derived-old-town-fan-cobble-normal",
      oldTownCobbleTexture,
      3.4,
    ),
    oldTownJunction: createNormalTextureFromLuma(
      "reference-derived-old-town-junction-setts-normal",
      oldTownJunctionTexture,
      3,
    ),
    asphaltRoad: createNormalTextureFromLuma(
      "procedural-asphalt-road-history-normal",
      asphaltRoadTexture,
      1.8,
    ),
    asphaltPath: createNormalTextureFromLuma(
      "reference-derived-general-asphalt-normal",
      asphaltPathTexture,
      1.8,
    ),
    oldTownPaver: createNormalTextureFromLuma(
      "reference-derived-old-town-stone-pavers-normal",
      oldTownPaverTexture,
      2.8,
    ),
    concrete: createNormalTextureFromLuma(
      "reference-derived-general-concrete-slabs-normal",
      concreteSlabTexture,
      2.1,
    ),
    cement: createNormalTextureFromLuma(
      "official-grpk-cement-concrete-road-normal",
      cementRoadTexture,
      2.1,
    ),
    aggregate: createNormalTextureFromLuma(
      "official-grpk-crushed-aggregate-road-normal",
      aggregateRoadTexture,
      2.6,
    ),
    gravel: createNormalTextureFromLuma(
      "official-grpk-gravel-road-normal",
      gravelRoadTexture,
      2.8,
    ),
    wood: createNormalTextureFromLuma(
      "official-grpk-wood-road-normal",
      woodRoadTexture,
      2.4,
    ),
    clayRoof: createNormalTextureFromLuma(
      "shared-clay-roof-tile-normal",
      roofTexture,
      3.2,
    ),
    metalRoof: createNormalTextureFromLuma(
      "shared-standing-seam-metal-roof-normal",
      metalRoofTexture,
      2.2,
    ),
    grass: createNormalTextureFromLuma(
      "reference-derived-vegetated-land-cover-normal",
      grassTexture,
      1.8,
    ),
    treeBark: createNormalTextureFromLuma(
      "shared-tree-bark-normal",
      treeBarkTexture,
      2.2,
    ),
    soil: createNormalTextureFromLuma(
      "source-positioned-central-street-tree-soil-normal",
      soilTexture,
      2.4,
    ),
  } as const;
  const normalScale = VISUAL_REALISM_CONFIG.normalScale;
  const facadeMaterials = new Map<string, MeshStandardMaterial>();
  const facadeNightIntensityFactors = new Map<
    MeshStandardMaterial,
    number
  >();
  const blankFacadeMaterials = new Map<string, MeshStandardMaterial>();
  const architecturalTrimMaterials = new Map<
    string,
    MeshStandardMaterial
  >();
  const panelJointMaterials = new Map<string, MeshStandardMaterial>();
  const roofMaterials = new Map<string, MeshStandardMaterial>();
  const facadeBaseColors = new Map<MeshStandardMaterial, Color>();
  const trimBaseColors = new Map<MeshStandardMaterial, Color>();
  const roofBaseColors = new Map<MeshStandardMaterial, Color>();
  const wettableSurfaceStates: WettableSurfaceState[] = [];
  let currentSeason: EnvironmentSeason = "summer";
  let currentSurfaceWetness01 = 0;
  let trafficSignalElapsedSeconds = 0;

  const terrain: Record<RuntimeTileLodLevel, MeshStandardMaterial> = {
    0: new MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      roughness: 0.99,
      metalness: 0,
    }),
    1: new MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      roughness: 1,
      metalness: 0,
    }),
    2: new MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      roughness: 1,
      metalness: 0,
    }),
  };
  const landCover = new MeshStandardMaterial({
    color: 0x606b56,
    map: grassTexture,
    normalMap: surfaceNormalMaps.grass,
    normalScale: materialNormalScale(
      realismEnabled,
      normalScale.landCover,
    ),
    roughness: 1,
    metalness: 0,
    transparent: false,
    opacity: 1,
    // Land-cover follows the shared runtime water-ground terrain. Let the
    // later water pass replace its colour inside the authored river ribbon.
    depthWrite: false,
    side: DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
  const groundCover = Object.fromEntries(
    (["woodland", "scrub", "meadow", "garden", "park"] as const).map(
      (family) => [family, landCover.clone()],
    ),
  ) as Record<GroundCoverMaterialFamily, MeshStandardMaterial>;
  for (const [family, material] of Object.entries(groundCover)) {
    material.name = `source-ground-cover:${family}`;
  }
  const hedge = new MeshStandardMaterial({
    color: 0x526047,
    roughness: 1,
    metalness: 0,
    flatShading: true,
  });
  const parkedVehicleBody = new MeshStandardMaterial({
    color: 0xffffff,
    vertexColors: true,
    roughness: 0.78,
    metalness: 0.08,
    flatShading: true,
  });
  const parcelBoundary = new MeshStandardMaterial({
    color: 0x485043,
    roughness: 1,
    metalness: 0,
    transparent: true,
    opacity: 0.42,
    depthWrite: false,
  });
  const streetSurfaces: Record<
    StreetSurfaceFamily,
    StreetSurfaceMaterialSet
  > = {
    old_town: {
      carriageway: new MeshStandardMaterial({
        color: 0x8c8d88,
        map: oldTownCobbleTexture,
        normalMap: surfaceNormalMaps.oldTownCobble,
        normalScale: materialNormalScale(realismEnabled, normalScale.oldTownStone),
        roughness: 0.99,
        metalness: 0,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
      junction: new MeshStandardMaterial({
        color: 0x92938e,
        map: oldTownJunctionTexture,
        normalMap: surfaceNormalMaps.oldTownJunction,
        normalScale: materialNormalScale(realismEnabled, normalScale.oldTownStone),
        roughness: 0.99,
        metalness: 0,
      }),
      sidewalk: new MeshStandardMaterial({
        color: 0xb0aea6,
        map: oldTownPaverTexture,
        normalMap: surfaceNormalMaps.oldTownPaver,
        normalScale: materialNormalScale(realismEnabled, normalScale.oldTownStone),
        roughness: 0.98,
        metalness: 0,
      }),
      curb: new MeshStandardMaterial({
        color: 0x96948c,
        map: oldTownPaverTexture,
        normalMap: surfaceNormalMaps.oldTownPaver,
        normalScale: materialNormalScale(realismEnabled, normalScale.oldTownStone),
        roughness: 0.99,
        metalness: 0,
        side: DoubleSide,
      }),
      pedestrian: new MeshStandardMaterial({
        color: 0xa6a39b,
        map: oldTownPaverTexture,
        normalMap: surfaceNormalMaps.oldTownPaver,
        normalScale: materialNormalScale(realismEnabled, normalScale.oldTownStone),
        roughness: 0.99,
        metalness: 0,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
    },
    general: {
      carriageway: new MeshStandardMaterial({
        color: 0x858886,
        map: asphaltRoadTexture,
        normalMap: surfaceNormalMaps.asphaltRoad,
        normalScale: materialNormalScale(realismEnabled, normalScale.asphalt),
        roughness: 0.99,
        metalness: 0,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
      junction: new MeshStandardMaterial({
        color: 0x898c89,
        map: asphaltRoadTexture,
        normalMap: surfaceNormalMaps.asphaltRoad,
        normalScale: materialNormalScale(realismEnabled, normalScale.asphalt),
        roughness: 0.99,
        metalness: 0,
      }),
      sidewalk: new MeshStandardMaterial({
        color: 0xb7b0a4,
        map: concreteSlabTexture,
        normalMap: surfaceNormalMaps.concrete,
        normalScale: materialNormalScale(realismEnabled, normalScale.concrete),
        roughness: 0.99,
        metalness: 0,
      }),
      curb: new MeshStandardMaterial({
        color: 0xafa697,
        map: concreteSlabTexture,
        normalMap: surfaceNormalMaps.concrete,
        normalScale: materialNormalScale(realismEnabled, normalScale.concrete),
        roughness: 0.99,
        metalness: 0,
        side: DoubleSide,
      }),
      pedestrian: new MeshStandardMaterial({
        color: 0xaaa397,
        map: concreteSlabTexture,
        normalMap: surfaceNormalMaps.concrete,
        normalScale: materialNormalScale(realismEnabled, normalScale.concrete),
        roughness: 0.99,
        metalness: 0,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
    },
  };
  const supplementalRoadSurfaceMaterials = {
    cement_concrete: new MeshStandardMaterial({
      name: "official-grpk-road-surface:cement-concrete",
      color: 0xaaa8a0,
      map: cementRoadTexture,
      normalMap: surfaceNormalMaps.cement,
      normalScale: materialNormalScale(realismEnabled, normalScale.concrete),
      roughness: 0.98,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    aggregate: new MeshStandardMaterial({
      name: "official-grpk-road-surface:crushed-aggregate",
      color: 0x908b7d,
      map: aggregateRoadTexture,
      normalMap: surfaceNormalMaps.aggregate,
      normalScale: materialNormalScale(realismEnabled, normalScale.aggregate),
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    gravel: new MeshStandardMaterial({
      name: "official-grpk-road-surface:gravel",
      color: 0x8d806d,
      map: gravelRoadTexture,
      normalMap: surfaceNormalMaps.gravel,
      normalScale: materialNormalScale(realismEnabled, normalScale.aggregate),
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    wood: new MeshStandardMaterial({
      name: "official-grpk-road-surface:wood",
      color: 0x66513e,
      map: woodRoadTexture,
      normalMap: surfaceNormalMaps.wood,
      normalScale: materialNormalScale(realismEnabled, normalScale.wood),
      roughness: 0.98,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
  } as const;
  const officialRoadSurfaces: Record<
    Exclude<RoadSurfaceClass, "default">,
    MeshStandardMaterial
  > = {
    asphalt: streetSurfaces.general.carriageway,
    cement_concrete: supplementalRoadSurfaceMaterials.cement_concrete,
    paving: streetSurfaces.old_town.carriageway,
    aggregate: supplementalRoadSurfaceMaterials.aggregate,
    gravel: supplementalRoadSurfaceMaterials.gravel,
    wood: supplementalRoadSurfaceMaterials.wood,
    other: streetSurfaces.general.carriageway,
  };
  const supplementalPedestrianSurfaceMaterials = {
    cycleway: new MeshStandardMaterial({
      name: "mapped-active-travel-surface:cycleway",
      color: 0x8f4b43,
      map: concreteSlabTexture,
      normalMap: surfaceNormalMaps.concrete,
      normalScale: materialNormalScale(realismEnabled, normalScale.concrete),
      roughness: 0.99,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -3,
      polygonOffsetUnits: -3,
    }),
    asphalt: new MeshStandardMaterial({
      name: "mapped-osm-sidewalk-surface:asphalt",
      color: 0x797a75,
      map: asphaltPathTexture,
      normalMap: surfaceNormalMaps.asphaltPath,
      normalScale: materialNormalScale(realismEnabled, normalScale.asphalt),
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    concrete: new MeshStandardMaterial({
      name: "mapped-osm-sidewalk-surface:concrete",
      color: 0xaaa397,
      map: concreteSlabTexture,
      normalMap: surfaceNormalMaps.concrete,
      normalScale: materialNormalScale(realismEnabled, normalScale.concrete),
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    paving: new MeshStandardMaterial({
      name: "mapped-osm-sidewalk-surface:paving",
      color: 0x85847a,
      map: oldTownPaverTexture,
      normalMap: surfaceNormalMaps.oldTownPaver,
      normalScale: materialNormalScale(realismEnabled, normalScale.oldTownStone),
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    sand: new MeshStandardMaterial({
      name: "mapped-osm-sidewalk-surface:sand",
      color: 0x9d8f73,
      map: aggregateRoadTexture,
      normalMap: surfaceNormalMaps.aggregate,
      normalScale: materialNormalScale(realismEnabled, normalScale.aggregate),
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    wood: new MeshStandardMaterial({
      name: "mapped-osm-sidewalk-surface:wood",
      color: 0x6f5943,
      map: woodRoadTexture,
      normalMap: surfaceNormalMaps.wood,
      normalScale: materialNormalScale(realismEnabled, normalScale.wood),
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
  } as const;
  const mappedPedestrianSurfaces: Record<
    Exclude<PedestrianSurfaceClass, "default">,
    MeshStandardMaterial
  > = {
    ...supplementalPedestrianSurfaceMaterials,
    other: streetSurfaces.general.pedestrian,
  };
  const wetSurface = VISUAL_REALISM_CONFIG.wetSurface;
  const wetSurfaceShadowColor = new Color(wetSurface.shadowColor);
  const updateWettableSurface = (
    state: Readonly<WettableSurfaceState>,
    visibleWetness: number,
  ): void => {
    state.material.color
      .copy(state.dryColor)
      .lerp(
        wetSurfaceShadowColor,
        state.darkening * visibleWetness,
      );
    state.material.roughness = state.dryRoughness +
      (state.wetRoughness - state.dryRoughness) * visibleWetness;
  };
  const registerWettableSurface = (
    material: MeshStandardMaterial,
    wetRoughness: number,
    darkening: number,
    realisticDryRoughness?: number,
  ): void => {
    if (realismEnabled && realisticDryRoughness !== undefined) {
      material.roughness = realisticDryRoughness;
    }
    const state = {
      material,
      dryColor: material.color.clone(),
      dryRoughness: material.roughness,
      wetRoughness,
      darkening,
    };
    wettableSurfaceStates.push(state);
    updateWettableSurface(
      state,
      realismEnabled ? currentSurfaceWetness01 : 0,
    );
  };
  for (const material of Object.values(terrain)) {
    registerWettableSurface(
      material,
      wetSurface.terrainRoughness,
      wetSurface.terrainDarkening,
      VISUAL_REALISM_CONFIG.drySurface.terrainRoughness,
    );
  }
  registerWettableSurface(
    landCover,
    wetSurface.landCoverRoughness,
    wetSurface.landCoverDarkening,
    VISUAL_REALISM_CONFIG.drySurface.landCoverRoughness,
  );
  for (const material of [
    streetSurfaces.general.carriageway,
    streetSurfaces.general.junction,
    supplementalPedestrianSurfaceMaterials.asphalt,
    supplementalPedestrianSurfaceMaterials.cycleway,
  ]) {
    registerWettableSurface(
      material,
      wetSurface.asphaltRoughness,
      wetSurface.asphaltDarkening,
      VISUAL_REALISM_CONFIG.drySurface.asphaltRoughness,
    );
  }
  for (const material of [
    streetSurfaces.old_town.carriageway,
    streetSurfaces.old_town.junction,
    streetSurfaces.old_town.sidewalk,
    streetSurfaces.old_town.curb,
    streetSurfaces.old_town.pedestrian,
    supplementalPedestrianSurfaceMaterials.paving,
  ]) {
    registerWettableSurface(
      material,
      wetSurface.stoneRoughness,
      wetSurface.stoneDarkening,
      VISUAL_REALISM_CONFIG.drySurface.stoneRoughness,
    );
  }
  for (const material of [
    streetSurfaces.general.sidewalk,
    streetSurfaces.general.curb,
    streetSurfaces.general.pedestrian,
    supplementalRoadSurfaceMaterials.cement_concrete,
    supplementalPedestrianSurfaceMaterials.concrete,
  ]) {
    registerWettableSurface(
      material,
      wetSurface.concreteRoughness,
      wetSurface.concreteDarkening,
      VISUAL_REALISM_CONFIG.drySurface.concreteRoughness,
    );
  }
  for (const material of [
    supplementalRoadSurfaceMaterials.aggregate,
    supplementalRoadSurfaceMaterials.gravel,
    supplementalPedestrianSurfaceMaterials.sand,
  ]) {
    registerWettableSurface(
      material,
      wetSurface.aggregateRoughness,
      wetSurface.aggregateDarkening,
      VISUAL_REALISM_CONFIG.drySurface.aggregateRoughness,
    );
  }
  for (const material of [
    supplementalRoadSurfaceMaterials.wood,
    supplementalPedestrianSurfaceMaterials.wood,
  ]) {
    registerWettableSurface(
      material,
      wetSurface.woodRoughness,
      wetSurface.woodDarkening,
      VISUAL_REALISM_CONFIG.drySurface.woodRoughness,
    );
  }
  const distantTransport: Record<
    StreetSurfaceFamily,
    DistantTransportMaterialSet
  > = {
    old_town: {
      carriageway: new MeshStandardMaterial({
        color: 0x666963,
        roughness: 1,
        metalness: 0,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
      pedestrian: new MeshStandardMaterial({
        color: 0x85847a,
        roughness: 1,
        metalness: 0,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
    },
    general: {
      carriageway: new MeshStandardMaterial({
        color: 0x6d716d,
        roughness: 1,
        metalness: 0,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
      pedestrian: new MeshStandardMaterial({
        color: 0x8c8980,
        roughness: 1,
        metalness: 0,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
    },
  };
  registerWettableSurface(
    distantTransport.old_town.carriageway,
    wetSurface.stoneRoughness,
    wetSurface.stoneDarkening,
    VISUAL_REALISM_CONFIG.drySurface.stoneRoughness,
  );
  registerWettableSurface(
    distantTransport.old_town.pedestrian,
    wetSurface.stoneRoughness,
    wetSurface.stoneDarkening,
    VISUAL_REALISM_CONFIG.drySurface.stoneRoughness,
  );
  registerWettableSurface(
    distantTransport.general.carriageway,
    wetSurface.asphaltRoughness,
    wetSurface.asphaltDarkening,
    VISUAL_REALISM_CONFIG.drySurface.asphaltRoughness,
  );
  registerWettableSurface(
    distantTransport.general.pedestrian,
    wetSurface.concreteRoughness,
    wetSurface.concreteDarkening,
    VISUAL_REALISM_CONFIG.drySurface.concreteRoughness,
  );
  const railway: RailwayMaterialSet = {
    ballast: new MeshStandardMaterial({
      name: "official-grpk-railway:ballast",
      color: 0x6b6861,
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    sleeper: new MeshStandardMaterial({
      name: "official-grpk-railway:sleeper",
      color: 0x665344,
      roughness: 0.9,
      metalness: 0.03,
      polygonOffset: true,
      polygonOffsetFactor: -3,
      polygonOffsetUnits: -3,
    }),
    steel: new MeshStandardMaterial({
      name: "official-grpk-railway:steel",
      color: 0xadb2ae,
      roughness: 0.23,
      metalness: 0.82,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4,
    }),
    catenary: new MeshStandardMaterial({
      name: "osm-enriched-railway:catenary-and-equipment",
      color: 0x737b77,
      roughness: 0.38,
      metalness: 0.68,
      side: DoubleSide,
    }),
    distant: new MeshStandardMaterial({
      name: "official-grpk-railway:distant-bed",
      color: 0x4f514d,
      roughness: 1,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
  };
  registerWettableSurface(
    railway.ballast,
    wetSurface.aggregateRoughness,
    wetSurface.aggregateDarkening,
    VISUAL_REALISM_CONFIG.drySurface.aggregateRoughness,
  );
  registerWettableSurface(
    railway.sleeper,
    wetSurface.woodRoughness,
    wetSurface.woodDarkening,
    VISUAL_REALISM_CONFIG.drySurface.woodRoughness,
  );
  registerWettableSurface(
    railway.steel,
    wetSurface.railRoughness,
    wetSurface.railDarkening,
  );
  registerWettableSurface(
    railway.catenary,
    wetSurface.railRoughness,
    wetSurface.railDarkening,
  );
  registerWettableSurface(
    railway.distant,
    wetSurface.aggregateRoughness,
    wetSurface.aggregateDarkening,
    VISUAL_REALISM_CONFIG.drySurface.aggregateRoughness,
  );
  const water = new ShaderMaterial({
    name: "animated-vilnius-river-water",
    transparent: false,
    opacity: 1,
    depthWrite: true,
    side: DoubleSide,
    fog: true,
    uniforms: UniformsUtils.merge([
      UniformsLib.fog,
      {
        waterTime: { value: 0 },
        deepColour: { value: new Color(0x244f5b) },
        shallowColour: { value: new Color(0x66848a) },
        skyColour: { value: new Color(0x9fb3b2) },
        nightFactor: { value: 0 },
        rippleScale: { value: 1 },
        rippleHeightM: { value: WATER_RENDER_CONFIG.rippleHeightM },
        flowSpeed: { value: WATER_RENDER_CONFIG.flowSpeed },
        secondaryFlowSpeed: {
          value: WATER_RENDER_CONFIG.secondaryFlowSpeed,
        },
        shorelineStart01: { value: WATER_RENDER_CONFIG.shorelineStart01 },
        wetEdgeStart01: { value: WATER_RENDER_CONFIG.wetEdgeStart01 },
        shorelineColourMix: {
          value: WATER_RENDER_CONFIG.shorelineColourMix,
        },
        skyReflectionStrength: {
          value: WATER_RENDER_CONFIG.skyReflectionStrength,
        },
        daylightGlintStrength: {
          value: WATER_RENDER_CONFIG.daylightGlintStrength,
        },
        nightBrightnessScale: {
          value: WATER_RENDER_CONFIG.nightBrightnessScale,
        },
      },
    ]),
    vertexShader: `
      attribute vec2 waterFlowDirection;
      uniform float waterTime;
      uniform float rippleScale;
      uniform float rippleHeightM;
      uniform float flowSpeed;
      uniform float secondaryFlowSpeed;
      varying vec3 vWorldPosition;
      varying float vRipple;
      varying vec2 vWaterUv;
      varying vec2 vFlowDirection;
      #include <fog_pars_vertex>
      void main() {
        vec4 world = modelMatrix * vec4(position, 1.0);
        vec2 flowDirection = normalize(waterFlowDirection);
        vec2 crossDirection = vec2(-flowDirection.y, flowDirection.x);
        float alongRiver = dot(world.xz, flowDirection);
        float acrossRiver = dot(world.xz, crossDirection);
        float first = sin(
          alongRiver * 0.055 - waterTime * flowSpeed
        );
        float second = sin(
          alongRiver * -0.031 + acrossRiver * 0.044 +
          waterTime * secondaryFlowSpeed
        );
        float third = sin(
          alongRiver * 0.017 - acrossRiver * 0.021 -
          waterTime * flowSpeed * 0.46
        );
        vRipple = first * 0.50 + second * 0.31 + third * 0.19;
        world.y += vRipple * rippleHeightM * rippleScale;
        vWorldPosition = world.xyz;
        vWaterUv = uv;
        vFlowDirection = flowDirection;
        vec4 mvPosition = viewMatrix * world;
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }
    `,
    fragmentShader: `
      uniform vec3 deepColour;
      uniform vec3 shallowColour;
      uniform vec3 skyColour;
      uniform float waterTime;
      uniform float nightFactor;
      uniform float flowSpeed;
      uniform float shorelineStart01;
      uniform float wetEdgeStart01;
      uniform float shorelineColourMix;
      uniform float skyReflectionStrength;
      uniform float daylightGlintStrength;
      uniform float nightBrightnessScale;
      varying vec3 vWorldPosition;
      varying float vRipple;
      varying vec2 vWaterUv;
      varying vec2 vFlowDirection;
      #include <fog_pars_fragment>
      void main() {
        vec3 eye = normalize(cameraPosition - vWorldPosition);
        float fresnel = pow(1.0 - clamp(abs(eye.y), 0.0, 1.0), 2.6);
        float bankDistance01 = abs(vWaterUv.y * 2.0 - 1.0);
        float shoreline = smoothstep(
          shorelineStart01,
          1.0,
          bankDistance01
        );
        float wetEdge = smoothstep(wetEdgeStart01, 1.0, bankDistance01);
        float alongRiver = dot(vWorldPosition.xz, vFlowDirection);
        float broadCurrent = sin(
          alongRiver * 0.012 - waterTime * flowSpeed * 0.38 +
          vRipple * 0.42
        ) * 0.5 + 0.5;
        float shallowMix = clamp(
          0.16 + shoreline * shorelineColourMix +
          broadCurrent * 0.055 + vRipple * 0.025,
          0.0,
          0.84
        );
        vec3 colour = mix(deepColour, shallowColour, shallowMix);
        float reflection = fresnel * skyReflectionStrength *
          (0.78 + broadCurrent * 0.22);
        colour = mix(colour, skyColour, reflection);
        float glintWave = sin(
          alongRiver * 0.081 - waterTime * flowSpeed * 0.72 +
          vRipple * 0.58
        ) * 0.5 + 0.5;
        float shimmer = pow(glintWave, 12.0) * (1.0 - shoreline * 0.72);
        colour += skyColour * shimmer * (1.0 - nightFactor) *
          daylightGlintStrength;
        // A thin, darker waterline visually seats the river against either a
        // natural bank or an authored retaining edge without inventing props.
        colour *= 1.0 - wetEdge * 0.16;
        colour *= mix(1.0, nightBrightnessScale, nightFactor);
        gl_FragColor = vec4(colour, 1.0);
        #include <fog_fragment>
      }
    `,
  });
  const diagnosticBuildingPlan = new MeshStandardMaterial({
    color: 0x8b7764,
    roughness: 1,
    metalness: 0,
    side: DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const lodBuildingBounds: Record<1 | 2, MeshStandardMaterial> = {
    1: new MeshStandardMaterial({
      color: 0x8f8376,
      roughness: 1,
      transparent: false,
      opacity: 1,
      depthWrite: true,
      side: DoubleSide,
    }),
    2: new MeshStandardMaterial({
      color: 0x86837d,
      roughness: 1,
      transparent: false,
      opacity: 1,
      depthWrite: true,
      side: DoubleSide,
    }),
  };
  const collisionLine = new LineBasicMaterial({
    color: 0xe08e67,
    transparent: true,
    opacity: 0.9,
    depthTest: false,
  });
  const tileDiagnosticLine = new LineBasicMaterial({
    color: 0xc0a170,
    transparent: true,
    opacity: 0.88,
    depthTest: false,
  });
  const window = new MeshStandardMaterial({
    color: realismEnabled
      ? VISUAL_REALISM_CONFIG.authoredGlassColor
      : 0x2c3538,
    roughness: 0.48,
    metalness: 0.04,
    emissive: realismEnabled
      ? VISUAL_REALISM_CONFIG.authoredWindowEmissionColor
      : 0xf1a963,
    emissiveIntensity: 0,
  });
  const windowUnlit = new MeshStandardMaterial({
    color: realismEnabled
      ? VISUAL_REALISM_CONFIG.authoredGlassColor
      : 0x2c3538,
    roughness: 0.48,
    metalness: 0.04,
    emissiveIntensity: 0,
  });
  const panelWindowUnlit = new MeshStandardMaterial({
    color: realismEnabled
      ? VISUAL_REALISM_CONFIG.panelAuthoredGlassColor
      : 0x2c3538,
    roughness: 0.52,
    metalness: 0.03,
    emissiveIntensity: 0,
  });
  const oldTownDoorLeaves = {
    timber: new MeshStandardMaterial({
      color: 0x51392e,
      roughness: 0.92,
      metalness: 0,
    }),
    painted: new MeshStandardMaterial({
      color: 0x2e4942,
      roughness: 0.88,
      metalness: 0,
    }),
  } as const satisfies Readonly<
    Record<OldTownDoorLeafStyle, MeshStandardMaterial>
  >;
  oldTownDoorLeaves.timber.name = "shared-old-town-door:timber";
  oldTownDoorLeaves.painted.name = "shared-old-town-door:painted";
  const oldTownPlanter = new MeshStandardMaterial({
    color: 0x5b4033,
    roughness: 1,
    metalness: 0,
  });
  oldTownPlanter.name = "shared-old-town-planter";
  const oldTownPortalLanternBulb = new MeshStandardMaterial({
    color: 0xc4ab82,
    roughness: 0.5,
    emissive: 0xffa24f,
    emissiveIntensity: 0,
  });
  oldTownPortalLanternBulb.name =
    "shared-old-town-portal-lantern-bulb";
  const buildingPassageLining = new MeshStandardMaterial({
    color: 0x595c58,
    roughness: 1,
    metalness: 0,
  });
  buildingPassageLining.name = "shared-building-passage-lining";
  const tunnelConcrete = new MeshStandardMaterial({
    color: 0x807b71,
    map: concreteSlabTexture,
    normalMap: surfaceNormalMaps.concrete,
    normalScale: materialNormalScale(realismEnabled, normalScale.concrete),
    roughness: 0.96,
    metalness: 0,
  });
  tunnelConcrete.name = "gelezinio-vilko-tunnel-concrete";
  const tunnelJoint = new MeshStandardMaterial({
    color: 0x292b28,
    roughness: 0.98,
    metalness: 0,
  });
  tunnelJoint.name = "gelezinio-vilko-tunnel-joints";
  const tunnelMetal = new MeshStandardMaterial({
    color: 0x343733,
    roughness: 0.72,
    metalness: 0.46,
  });
  tunnelMetal.name = "gelezinio-vilko-tunnel-metalwork";
  const tunnelLight = new MeshStandardMaterial({
    color: 0xffdfb4,
    emissive: 0xffa64f,
    emissiveIntensity: 2.6,
    roughness: 0.34,
    metalness: 0.04,
  });
  tunnelLight.name = "gelezinio-vilko-tunnel-luminaires";
  const tunnelDamp = new MeshStandardMaterial({
    color: 0x30352f,
    roughness: 0.9,
    metalness: 0,
  });
  tunnelDamp.name = "gelezinio-vilko-tunnel-damp-band";
  const lampBulb = new MeshStandardMaterial({
    color: 0xd9c9a9,
    roughness: 0.45,
    emissive: 0xffbc6b,
    emissiveIntensity: 0,
  });
  const lampPool = new MeshBasicMaterial({
    color: 0xffc27d,
    map: lampPoolTexture,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: AdditiveBlending,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  });
  const streetPole = new MeshStandardMaterial({
    color: 0x343938,
    roughness: 0.88,
    metalness: 0.22,
  });
  const busStopBlue = new MeshStandardMaterial({
    color: 0x155ea8,
    emissive: 0x08264a,
    emissiveIntensity: 0.24,
    roughness: 0.68,
    metalness: 0.06,
  });
  busStopBlue.name = "shared-bus-stop:judu-blue";
  const busStopIvory = new MeshStandardMaterial({
    color: 0xf0efe6,
    emissive: 0x4c4b45,
    emissiveIntensity: 0.18,
    roughness: 0.82,
    metalness: 0,
  });
  busStopIvory.name = "shared-bus-stop:ivory-panel";
  const busStopGlass = new MeshStandardMaterial({
    color: 0x9fbfc7,
    roughness: 0.18,
    metalness: 0.02,
    transparent: true,
    opacity: 0.24,
    depthWrite: false,
    side: DoubleSide,
  });
  busStopGlass.name = "shared-bus-stop:shelter-glass";
  const busStopSeat = new MeshStandardMaterial({
    color: 0x796b58,
    roughness: 0.9,
    metalness: 0.02,
  });
  busStopSeat.name = "shared-bus-stop:weathered-seat-slats";
  const busStopTactile = new MeshStandardMaterial({
    color: 0xb9a34f,
    roughness: 0.92,
    metalness: 0,
    polygonOffset: true,
    polygonOffsetFactor: -5,
    polygonOffsetUnits: -5,
  });
  busStopTactile.name = "shared-bus-stop:tactile-warning-paving";
  const roadMarking = new MeshStandardMaterial({
    color: 0xe8e7df,
    roughness: 0.88,
    metalness: 0,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
  roadMarking.name = "mapped-road:crossing-marking";
  registerWettableSurface(
    roadMarking,
    wetSurface.markingRoughness,
    wetSurface.markingDarkening,
    VISUAL_REALISM_CONFIG.drySurface.markingRoughness,
  );
  const laneMarking = new MeshStandardMaterial({
    color: 0xe3e1d8,
    roughness: 0.94,
    metalness: 0,
    polygonOffset: true,
    polygonOffsetFactor: -5,
    polygonOffsetUnits: -5,
  });
  laneMarking.name = "official-road:inferred-longitudinal-marking";
  const roadSignBlue = new MeshStandardMaterial({
    color: 0x15579b,
    emissive: 0x08213b,
    emissiveIntensity: 0.12,
    roughness: 0.68,
    metalness: 0.04,
  });
  roadSignBlue.name = "shared-road-sign:reflective-blue";
  const roadSignIvory = new MeshStandardMaterial({
    color: 0xf0efe8,
    emissive: 0x5c5b56,
    emissiveIntensity: 0.16,
    roughness: 0.78,
    metalness: 0,
  });
  roadSignIvory.name = "shared-road-sign:reflective-ivory";
  const roadSignInk = new MeshStandardMaterial({
    color: 0x181b1b,
    roughness: 0.9,
    metalness: 0,
  });
  roadSignInk.name = "shared-road-sign:symbol-ink";
  const roadSignRed = new MeshStandardMaterial({
    color: 0xb9342f,
    emissive: 0x43110f,
    emissiveIntensity: 0.13,
    roughness: 0.72,
    metalness: 0.02,
  });
  roadSignRed.name = "shared-road-sign:reflective-red";
  const roadSignYellow = new MeshStandardMaterial({
    color: 0xe6c74b,
    emissive: 0x51430e,
    emissiveIntensity: 0.12,
    roughness: 0.76,
    metalness: 0.01,
  });
  roadSignYellow.name = "shared-road-sign:reflective-yellow";
  const roadSignBack = new MeshStandardMaterial({
    color: 0x777b77,
    roughness: 0.86,
    metalness: 0.22,
  });
  roadSignBack.name = "shared-road-sign:galvanized-back";
  const trafficSignalHousing = new MeshStandardMaterial({
    color: 0x252a29,
    roughness: 0.82,
    metalness: 0.2,
  });
  trafficSignalHousing.name = "shared-traffic-signal:housing-and-pole";
  function trafficSignalLensMaterial(
    name: string,
    colour: number,
  ): MeshStandardMaterial {
    const material = new MeshStandardMaterial({
      color: new Color(colour).multiplyScalar(0.24),
      emissive: colour,
      emissiveIntensity: TRAFFIC_SIGNAL_RENDER_CONFIG.inactiveLensIntensity,
      roughness: 0.38,
      metalness: 0,
    });
    material.name = name;
    return material;
  }
  const trafficSignalLenses = {
    "axis-a": {
      red: trafficSignalLensMaterial(
        "shared-traffic-signal:axis-a:red",
        0xe34036,
      ),
      amber: trafficSignalLensMaterial(
        "shared-traffic-signal:axis-a:amber",
        0xf3a832,
      ),
      green: trafficSignalLensMaterial(
        "shared-traffic-signal:axis-a:green",
        0x43b568,
      ),
    },
    "axis-b": {
      red: trafficSignalLensMaterial(
        "shared-traffic-signal:axis-b:red",
        0xe34036,
      ),
      amber: trafficSignalLensMaterial(
        "shared-traffic-signal:axis-b:amber",
        0xf3a832,
      ),
      green: trafficSignalLensMaterial(
        "shared-traffic-signal:axis-b:green",
        0x43b568,
      ),
    },
  } as const satisfies Readonly<
    Record<TrafficSignalPhaseGroup, TrafficSignalLensMaterialSet>
  >;
  const treeTrunk = new MeshStandardMaterial({
    color: 0x4b3b2f,
    map: treeBarkTexture,
    normalMap: surfaceNormalMaps.treeBark,
    normalScale: materialNormalScale(realismEnabled, normalScale.treeBark),
    roughness: 1,
    metalness: 0,
  });
  const treeBranch = new MeshStandardMaterial({
    color: 0x4b3b2f,
    map: treeBarkTexture,
    normalMap: surfaceNormalMaps.treeBark,
    normalScale: materialNormalScale(realismEnabled, normalScale.treeBark),
    roughness: 1,
    metalness: 0,
  });
  const treeBirchTrunk = new MeshStandardMaterial({
    color: 0xc2c0b5,
    map: treeBarkTexture,
    normalMap: surfaceNormalMaps.treeBark,
    normalScale: materialNormalScale(realismEnabled, normalScale.treeBark),
    roughness: 1,
    metalness: 0,
  });
  const treeEvergreenTrunk = new MeshStandardMaterial({
    color: 0x6f4d35,
    map: treeBarkTexture,
    normalMap: surfaceNormalMaps.treeBark,
    normalScale: materialNormalScale(realismEnabled, normalScale.treeBark),
    roughness: 1,
    metalness: 0,
  });
  const treeSoil = new MeshStandardMaterial({
    name: "source-positioned-central-street-tree-soil",
    color: 0x625a43,
    map: soilTexture,
    normalMap: surfaceNormalMaps.soil,
    normalScale: materialNormalScale(realismEnabled, normalScale.aggregate),
    roughness: 1,
    metalness: 0,
  });
  registerWettableSurface(
    treeSoil,
    wetSurface.aggregateRoughness,
    wetSurface.aggregateDarkening,
    VISUAL_REALISM_CONFIG.drySurface.aggregateRoughness,
  );
  const treeCrownDark = new MeshStandardMaterial({
    color: 0x38533e,
    roughness: 1,
    metalness: 0,
    transparent: true,
    opacity: 1,
  });
  const treeCrownLight = new MeshStandardMaterial({
    color: 0x506a49,
    roughness: 1,
    metalness: 0,
    transparent: true,
    opacity: 1,
  });
  const treeEvergreenDark = new MeshStandardMaterial({
    color: 0x2d4a39,
    roughness: 1,
    metalness: 0,
  });
  const treeEvergreenLight = new MeshStandardMaterial({
    color: 0x426348,
    roughness: 1,
    metalness: 0,
  });
  const treeBlossom = new MeshStandardMaterial({
    color: 0xe7c3bb,
    roughness: 1,
    metalness: 0,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    visible: false,
  });
  const treeSnow = new MeshStandardMaterial({
    color: 0xe5e8e5,
    roughness: 0.96,
    metalness: 0,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    visible: false,
  });

  function applySeasonalTint(
    material: MeshStandardMaterial,
    baseColor: Readonly<Color>,
    tint: number,
    strength: number,
  ): void {
    material.color.copy(baseColor).lerp(new Color(tint), strength);
  }

  function applySurfaceWetness(wetness01: number, force = false): void {
    const clamped = Number.isFinite(wetness01)
      ? Math.max(0, Math.min(1, wetness01))
      : currentSurfaceWetness01;
    if (!force && Math.abs(clamped - currentSurfaceWetness01) < 0.001) {
      return;
    }
    currentSurfaceWetness01 = clamped;
    const visibleWetness = realismEnabled ? clamped : 0;
    for (const state of wettableSurfaceStates) {
      updateWettableSurface(state, visibleWetness);
    }
  }

  function applySeason(season: EnvironmentSeason): void {
    currentSeason = season;
    // Season changes can arrive while wetness is visible. Restore every
    // registered material to its dry state first so temporary darkening never
    // becomes the next season's baseline (notably for rails and markings,
    // whose dry colours are not otherwise rewritten below).
    for (const state of wettableSurfaceStates) {
      state.material.color.copy(state.dryColor);
      state.material.roughness = state.dryRoughness;
    }
    const profile = SEASONAL_SURFACE_PALETTES[season];
    for (const material of Object.values(terrain)) {
      material.color.setHex(profile.terrain);
      if (material.vertexColors !== profile.terrainUsesVertexColors) {
        material.vertexColors = profile.terrainUsesVertexColors;
        material.needsUpdate = true;
      }
    }
    landCover.color.setHex(profile.landCover);
    const seasonalGreen = new Color(profile.landCover);
    groundCover.woodland.color.copy(seasonalGreen).offsetHSL(
      -0.012,
      0.04,
      -0.075,
    );
    groundCover.scrub.color.copy(seasonalGreen).offsetHSL(
      -0.004,
      0.035,
      -0.025,
    );
    groundCover.meadow.color.copy(seasonalGreen).offsetHSL(
      0.016,
      -0.025,
      0.055,
    );
    groundCover.garden.color.copy(seasonalGreen).offsetHSL(
      -0.018,
      0.06,
      0.012,
    );
    groundCover.park.color.copy(seasonalGreen).offsetHSL(
      0.006,
      0.012,
      0.032,
    );
    hedge.color.copy(groundCover.woodland.color).offsetHSL(0, 0.025, 0.018);
    const oldTown = streetSurfaces.old_town;
    oldTown.carriageway.color.setHex(profile.oldTownCarriageway);
    oldTown.junction.color.setHex(profile.oldTownJunction);
    oldTown.sidewalk.color.setHex(profile.oldTownSidewalk);
    oldTown.curb.color.setHex(profile.oldTownCurb);
    oldTown.pedestrian.color.setHex(profile.oldTownPedestrian);
    const general = streetSurfaces.general;
    general.carriageway.color.setHex(profile.generalCarriageway);
    general.junction.color.setHex(profile.generalJunction);
    general.sidewalk.color.setHex(profile.generalSidewalk);
    general.curb.color.setHex(profile.generalCurb);
    general.pedestrian.color.setHex(profile.generalPedestrian);
    supplementalRoadSurfaceMaterials.cement_concrete.color
      .setHex(profile.generalCarriageway)
      .lerp(new Color(profile.generalSidewalk), 0.55);
    supplementalRoadSurfaceMaterials.aggregate.color
      .setHex(profile.generalCarriageway)
      .lerp(new Color(0x8f876f), 0.42);
    supplementalRoadSurfaceMaterials.gravel.color
      .setHex(profile.generalCarriageway)
      .lerp(new Color(0x8c765a), 0.52);
    supplementalRoadSurfaceMaterials.wood.color
      .setHex(profile.generalCarriageway)
      .lerp(new Color(0x604831), 0.68);
    supplementalPedestrianSurfaceMaterials.asphalt.color
      .setHex(profile.generalPedestrian)
      .lerp(new Color(profile.generalCarriageway), 0.58);
    supplementalPedestrianSurfaceMaterials.cycleway.color
      .setHex(0x8f4b43)
      .lerp(
        new Color(profile.generalPedestrian),
        season === "winter" ? 0.42 : 0.08,
      );
    supplementalPedestrianSurfaceMaterials.concrete.color
      .setHex(profile.generalPedestrian);
    supplementalPedestrianSurfaceMaterials.paving.color
      .setHex(profile.oldTownPedestrian);
    supplementalPedestrianSurfaceMaterials.sand.color
      .setHex(profile.generalPedestrian)
      .lerp(new Color(0x9d8865), 0.55);
    supplementalPedestrianSurfaceMaterials.wood.color
      .setHex(profile.generalPedestrian)
      .lerp(new Color(0x684d34), 0.72);
    distantTransport.old_town.carriageway.color.setHex(
      profile.oldTownDistantCarriageway,
    );
    distantTransport.old_town.pedestrian.color.setHex(
      profile.oldTownDistantPedestrian,
    );
    distantTransport.general.carriageway.color.setHex(
      profile.generalDistantCarriageway,
    );
    distantTransport.general.pedestrian.color.setHex(
      profile.generalDistantPedestrian,
    );
    water.uniforms.deepColour?.value.setHex(profile.waterDeep);
    water.uniforms.shallowColour?.value.setHex(profile.waterShallow);
    water.uniforms.skyColour?.value.setHex(profile.waterSky);
    if (water.uniforms.rippleScale !== undefined) {
      water.uniforms.rippleScale.value = profile.waterRippleScale;
    }
    treeTrunk.color.setHex(profile.treeTrunk);
    treeBranch.color.setHex(profile.treeBranch);
    treeBirchTrunk.color.setHex(profile.treeBirchTrunk);
    treeEvergreenTrunk.color.setHex(profile.treeEvergreenTrunk);
    treeCrownDark.color.setHex(profile.treeCrownDark);
    treeCrownDark.opacity = profile.treeCrownOpacity;
    treeCrownLight.color.setHex(profile.treeCrownLight);
    treeCrownLight.opacity = profile.treeCrownOpacity;
    treeEvergreenDark.color.setHex(profile.treeEvergreenDark);
    treeEvergreenLight.color.setHex(profile.treeEvergreenLight);
    treeBlossom.color.setHex(profile.treeBlossom);
    treeBlossom.opacity = profile.treeBlossomOpacity;
    treeBlossom.visible = profile.treeBlossomOpacity > 0.001;
    treeSnow.color.setHex(profile.treeSnow);
    treeSnow.opacity = profile.treeSnowOpacity;
    treeSnow.visible = profile.treeSnowOpacity > 0.001;
    for (const [material, baseColor] of facadeBaseColors) {
      applySeasonalTint(
        material,
        baseColor,
        profile.facadeTint,
        profile.facadeTintStrength,
      );
    }
    for (const [material, baseColor] of trimBaseColors) {
      applySeasonalTint(
        material,
        baseColor,
        profile.trimTint,
        profile.trimTintStrength,
      );
    }
    for (const [material, baseColor] of roofBaseColors) {
      applySeasonalTint(
        material,
        baseColor,
        profile.roofTint,
        profile.roofTintStrength,
      );
    }
    for (const state of wettableSurfaceStates) {
      state.dryColor.copy(state.material.color);
    }
    applySurfaceWetness(currentSurfaceWetness01, true);
  }

  function applyTrafficSignalPhase(): void {
    const config = TRAFFIC_SIGNAL_RENDER_CONFIG;
    const halfCycleSeconds =
      config.greenSeconds + config.amberSeconds + config.allRedSeconds;
    for (const phaseGroup of ["axis-a", "axis-b"] as const) {
      const offsetSeconds = phaseGroup === "axis-a" ? 0 : halfCycleSeconds;
      const localSeconds =
        (trafficSignalElapsedSeconds + offsetSeconds) % config.cycleSeconds;
      const active = localSeconds < config.greenSeconds
        ? "green"
        : localSeconds < config.greenSeconds + config.amberSeconds
          ? "amber"
          : "red";
      for (const [colour, material] of Object.entries(
        trafficSignalLenses[phaseGroup],
      )) {
        material.emissiveIntensity = colour === active
          ? config.activeLensIntensity
          : config.inactiveLensIntensity;
      }
    }
  }

  applyTrafficSignalPhase();

  return {
    terrain,
    landCover,
    groundCover,
    hedge,
    parcelBoundary,
    parkedVehicleBody,
    streetSurfaces,
    officialRoadSurfaces,
    mappedPedestrianSurfaces,
    distantTransport,
    railway,
    water,
    diagnosticBuildingPlan,
    lodBuildingBounds,
    collisionLine,
    tileDiagnosticLine,
    window,
    windowUnlit,
    panelWindowUnlit,
    oldTownDoorLeaves,
    oldTownPlanter,
    oldTownPortalLanternBulb,
    buildingPassageLining,
    tunnelConcrete,
    tunnelJoint,
    tunnelMetal,
    tunnelLight,
    tunnelDamp,
    lampBulb,
    lampPool,
    streetPole,
    busStopBlue,
    busStopIvory,
    busStopGlass,
    busStopSeat,
    busStopTactile,
    roadMarking,
    laneMarking,
    roadSignBlue,
    roadSignIvory,
    roadSignInk,
    roadSignRed,
    roadSignYellow,
    roadSignBack,
    trafficSignalHousing,
    trafficSignalLenses,
    treeTrunk,
    treeBranch,
    treeBirchTrunk,
    treeEvergreenTrunk,
    treeSoil,
    treeCrownDark,
    treeCrownLight,
    treeEvergreenDark,
    treeEvergreenLight,
    treeBlossom,
    treeSnow,
    facade(spec): MeshStandardMaterial {
      const nightVariant =
        spec.seed % BUILDING_WINDOW_NIGHT_INTENSITY_FACTORS.length;
      const key =
        `${spec.styleFamily}:${spec.facade.palette.id}:night-${nightVariant}`;
      const existing = facadeMaterials.get(key);
      if (existing !== undefined) {
        return existing;
      }
      const atlas = spec.styleFamily === "soviet_panel"
        ? panelAtlas
        : spec.styleFamily === "naujamiestis_industrial"
          ? industrialAtlas
        : spec.styleFamily === "antakalnis_suburban"
          ? spec.facade.surface === "painted_timber"
            ? timberAtlas
            : oldTownAtlas
        : spec.styleFamily === "zverynas" || spec.styleFamily === "snipiskes"
          ? timberAtlas
          : oldTownAtlas;
      const facadeNormal = spec.styleFamily === "soviet_panel"
        ? facadeNormalMaps.soviet_panel
        : spec.styleFamily === "naujamiestis_industrial"
          ? facadeNormalMaps.industrial
          : spec.styleFamily === "antakalnis_suburban" &&
              spec.facade.surface === "painted_timber"
            ? facadeNormalMaps.timber
            : spec.styleFamily === "zverynas" ||
                spec.styleFamily === "snipiskes"
              ? facadeNormalMaps.timber
              : facadeNormalMaps.old_town;
      const material = new MeshStandardMaterial({
        color: new Color(spec.facade.palette.wall),
        map: atlas.diffuse,
        normalMap: facadeNormal,
        normalScale: materialNormalScale(realismEnabled, normalScale.facade),
        emissive: realismEnabled
          ? VISUAL_REALISM_CONFIG.facadeWindowEmissionColor
          : 0xffb36b,
        emissiveMap: atlas.emissive,
        emissiveIntensity: 0,
        roughnessMap: atlas.roughness,
        roughness: spec.styleFamily === "soviet_panel"
          ? 0.98
          : spec.styleFamily === "naujamiestis_industrial"
            ? 0.96
            : spec.styleFamily === "antakalnis_suburban" &&
                spec.facade.surface === "painted_timber"
              ? 0.97
            : 0.94,
        metalness: 0,
      });
      material.name = `shared-facade:${key}`;
      facadeMaterials.set(key, material);
      facadeNightIntensityFactors.set(
        material,
        BUILDING_WINDOW_NIGHT_INTENSITY_FACTORS[nightVariant] ?? 0,
      );
      facadeBaseColors.set(material, material.color.clone());
      const profile = SEASONAL_SURFACE_PALETTES[currentSeason];
      applySeasonalTint(
        material,
        facadeBaseColors.get(material)!,
        profile.facadeTint,
        profile.facadeTintStrength,
      );
      return material;
    },
    blankFacade(spec): MeshStandardMaterial {
      const key = `${spec.styleFamily}:${spec.facade.palette.id}`;
      const existing = blankFacadeMaterials.get(key);
      if (existing !== undefined) {
        return existing;
      }
      const material = new MeshStandardMaterial({
        color: new Color(spec.facade.palette.wall),
        map: plasterTextures.diffuse,
        normalMap: plasterTextures.normal,
        normalScale: materialNormalScale(
          realismEnabled,
          normalScale.plasterMask,
        ),
        roughness: spec.styleFamily === "soviet_panel" ? 0.98 : 0.94,
        metalness: 0,
      });
      material.name = `shared-blank-facade:${key}`;
      blankFacadeMaterials.set(key, material);
      facadeBaseColors.set(material, material.color.clone());
      const profile = SEASONAL_SURFACE_PALETTES[currentSeason];
      applySeasonalTint(
        material,
        facadeBaseColors.get(material)!,
        profile.facadeTint,
        profile.facadeTintStrength,
      );
      return material;
    },
    architecturalTrim(spec): MeshStandardMaterial {
      const key = `${spec.styleFamily}:${spec.facade.palette.id}`;
      const existing = architecturalTrimMaterials.get(key);
      if (existing !== undefined) {
        return existing;
      }
      const trimColor = new Color(spec.facade.palette.wall).lerp(
        new Color(spec.facade.palette.trim),
        0.62,
      );
      const material = new MeshStandardMaterial({
        color: trimColor,
        normalMap: plasterTextures.normal,
        normalScale: materialNormalScale(
          realismEnabled,
          normalScale.architecturalTrim,
        ),
        roughness: 0.9,
        metalness: 0,
      });
      material.name = `shared-architectural-trim:${key}`;
      architecturalTrimMaterials.set(key, material);
      trimBaseColors.set(material, material.color.clone());
      const profile = SEASONAL_SURFACE_PALETTES[currentSeason];
      applySeasonalTint(
        material,
        trimBaseColors.get(material)!,
        profile.trimTint,
        profile.trimTintStrength,
      );
      return material;
    },
    panelJoint(spec): MeshStandardMaterial {
      const key = `${spec.styleFamily}:${spec.facade.palette.id}`;
      const existing = panelJointMaterials.get(key);
      if (existing !== undefined) {
        return existing;
      }
      const jointColor = new Color(spec.facade.palette.wall)
        .lerp(new Color(0x565b58), 0.46);
      const material = new MeshStandardMaterial({
        color: jointColor,
        roughness: 1,
        metalness: 0,
      });
      material.name = `shared-panel-joint:${key}`;
      panelJointMaterials.set(key, material);
      trimBaseColors.set(material, material.color.clone());
      const profile = SEASONAL_SURFACE_PALETTES[currentSeason];
      applySeasonalTint(
        material,
        trimBaseColors.get(material)!,
        profile.facadeTint,
        profile.facadeTintStrength,
      );
      return material;
    },
    roof(spec): MeshStandardMaterial {
      const key = `${spec.styleFamily}:${spec.roof.paletteId}`;
      const existing = roofMaterials.get(key);
      if (existing !== undefined) {
        return existing;
      }
      const isMetalRoof = spec.roof.paletteId === "weathered_metal" ||
        spec.roof.paletteId === "dark_metal";
      const isConcreteRoof = spec.roof.paletteId === "concrete_cap";
      const roofSurface = isConcreteRoof
        ? null
        : isMetalRoof
          ? metalRoofTexture
          : roofTexture;
      const roofNormal = isConcreteRoof
        ? surfaceNormalMaps.concrete
        : isMetalRoof
          ? surfaceNormalMaps.metalRoof
          : surfaceNormalMaps.clayRoof;
      const material = new MeshStandardMaterial({
        color: spec.roof.paletteId === "aged_clay"
          ? 0x7e5240
          : spec.roof.paletteId === "muted_clay"
            ? 0x8a5c49
            : spec.roof.paletteId === "weathered_metal"
              ? 0x68736f
              : spec.roof.paletteId === "dark_metal"
                ? 0x454d4a
                : 0x747670,
        map: roofSurface,
        normalMap: roofNormal,
        normalScale: materialNormalScale(
          realismEnabled,
          isConcreteRoof
            ? normalScale.concrete
            : isMetalRoof
              ? normalScale.metalRoof
              : normalScale.clayRoof,
        ),
        roughness: realismEnabled
          ? isConcreteRoof
            ? VISUAL_REALISM_CONFIG.drySurface.concreteRoughness
            : isMetalRoof
              ? VISUAL_REALISM_CONFIG.roofSurface.metalRoughness
              : VISUAL_REALISM_CONFIG.roofSurface.clayRoughness
          : isMetalRoof
            ? 0.82
            : 0.96,
        metalness: isMetalRoof && realismEnabled
          ? VISUAL_REALISM_CONFIG.roofSurface.metalness
          : isMetalRoof
            ? 0.12
            : 0,
        side: DoubleSide,
      });
      material.name = `shared-roof:${key}`;
      roofMaterials.set(key, material);
      roofBaseColors.set(material, material.color.clone());
      const profile = SEASONAL_SURFACE_PALETTES[currentSeason];
      applySeasonalTint(
        material,
        roofBaseColors.get(material)!,
        profile.roofTint,
        profile.roofTintStrength,
      );
      registerWettableSurface(
        material,
        isConcreteRoof
          ? wetSurface.concreteRoughness
          : isMetalRoof
            ? wetSurface.metalRoofRoughness
            : wetSurface.clayRoofRoughness,
        isConcreteRoof
          ? wetSurface.concreteDarkening
          : isMetalRoof
            ? wetSurface.metalRoofDarkening
            : wetSurface.clayRoofDarkening,
      );
      return material;
    },
    setSeason(season): void {
      applySeason(season);
    },
    setNightFactor(nightFactor): void {
      const clamped = Math.max(0, Math.min(1, nightFactor));
      const facadeEmissionScale = realismEnabled
        ? VISUAL_REALISM_CONFIG.facadeWindowEmissionScale
        : 1;
      const authoredEmissionScale = realismEnabled
        ? VISUAL_REALISM_CONFIG.authoredWindowEmissionScale
        : 1;
      for (const material of facadeMaterials.values()) {
        material.emissiveIntensity =
          clamped * 0.65 * facadeEmissionScale *
          (facadeNightIntensityFactors.get(material) ?? 0);
      }
      window.emissiveIntensity =
        clamped * AUTHORED_WINDOW_NIGHT_INTENSITY * authoredEmissionScale;
      oldTownPortalLanternBulb.emissiveIntensity = clamped * 2.2;
      lampBulb.emissiveIntensity = clamped * 4.2;
      lampPool.opacity = clamped * clamped * 0.46;
      const waterNight = water.uniforms.nightFactor;
      if (waterNight !== undefined) {
        waterNight.value = clamped;
      }
    },
    setSurfaceWetness(wetness01): void {
      applySurfaceWetness(wetness01);
    },
    advanceWaterTime(deltaSeconds): void {
      if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
        return;
      }
      const time = water.uniforms.waterTime;
      if (time !== undefined) {
        time.value = (Number(time.value) + deltaSeconds) % 10_000;
      }
    },
    advanceTrafficSignalTime(deltaSeconds): void {
      if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
        return;
      }
      trafficSignalElapsedSeconds =
        (trafficSignalElapsedSeconds + deltaSeconds) %
          TRAFFIC_SIGNAL_RENDER_CONFIG.cycleSeconds;
      applyTrafficSignalPhase();
    },
    dispose(): void {
      for (const material of [
        ...Object.values(terrain),
        landCover,
        ...Object.values(groundCover),
        hedge,
        parcelBoundary,
        parkedVehicleBody,
        ...Object.values(streetSurfaces).flatMap((family) => [
          family.carriageway,
          family.junction,
          family.sidewalk,
          family.curb,
          family.pedestrian,
        ]),
        ...Object.values(supplementalRoadSurfaceMaterials),
        ...Object.values(supplementalPedestrianSurfaceMaterials),
        ...Object.values(distantTransport).flatMap((family) => [
          family.carriageway,
          family.pedestrian,
        ]),
        ...Object.values(railway),
        water,
        diagnosticBuildingPlan,
        ...Object.values(lodBuildingBounds),
        collisionLine,
        tileDiagnosticLine,
        window,
        windowUnlit,
        panelWindowUnlit,
        ...Object.values(oldTownDoorLeaves),
        oldTownPlanter,
        oldTownPortalLanternBulb,
        buildingPassageLining,
        tunnelConcrete,
        tunnelJoint,
        tunnelMetal,
        tunnelLight,
        tunnelDamp,
        lampBulb,
        lampPool,
        streetPole,
        busStopBlue,
        busStopIvory,
        busStopGlass,
        busStopSeat,
        busStopTactile,
        roadMarking,
        laneMarking,
        roadSignBlue,
        roadSignIvory,
        roadSignInk,
        roadSignRed,
        roadSignYellow,
        roadSignBack,
        trafficSignalHousing,
        ...Object.values(trafficSignalLenses).flatMap((signal) => [
          signal.red,
          signal.amber,
          signal.green,
        ]),
        treeTrunk,
        treeBranch,
        treeBirchTrunk,
        treeEvergreenTrunk,
        treeSoil,
        treeCrownDark,
        treeCrownLight,
        treeEvergreenDark,
        treeEvergreenLight,
        treeBlossom,
        treeSnow,
        ...facadeMaterials.values(),
        ...blankFacadeMaterials.values(),
        ...architecturalTrimMaterials.values(),
        ...panelJointMaterials.values(),
        ...roofMaterials.values(),
      ]) {
        material.dispose();
      }
      oldTownAtlas.diffuse.dispose();
      oldTownAtlas.emissive.dispose();
      oldTownAtlas.roughness.dispose();
      panelAtlas.diffuse.dispose();
      panelAtlas.emissive.dispose();
      panelAtlas.roughness.dispose();
      timberAtlas.diffuse.dispose();
      timberAtlas.emissive.dispose();
      timberAtlas.roughness.dispose();
      industrialAtlas.diffuse.dispose();
      industrialAtlas.emissive.dispose();
      industrialAtlas.roughness.dispose();
      plasterTextures.diffuse.dispose();
      plasterTextures.normal.dispose();
      for (const texture of Object.values(facadeNormalMaps)) {
        texture.dispose();
      }
      for (const texture of [
        oldTownCobbleTexture,
        oldTownJunctionTexture,
        asphaltRoadTexture,
        asphaltPathTexture,
        oldTownPaverTexture,
        concreteSlabTexture,
        cementRoadTexture,
        aggregateRoadTexture,
        gravelRoadTexture,
        woodRoadTexture,
        roofTexture,
        metalRoofTexture,
        grassTexture,
        treeBarkTexture,
        soilTexture,
        lampPoolTexture,
        ...Object.values(surfaceNormalMaps),
      ]) {
        texture.dispose();
      }
      facadeMaterials.clear();
      facadeNightIntensityFactors.clear();
      blankFacadeMaterials.clear();
      architecturalTrimMaterials.clear();
      panelJointMaterials.clear();
      roofMaterials.clear();
      wettableSurfaceStates.length = 0;
      facadeBaseColors.clear();
      trimBaseColors.clear();
      roofBaseColors.clear();
    },
  };
}
